import { useState } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Button,
  Skeleton,
  SimpleGrid,
  Badge,
  Drawer,
  Modal,
  Progress,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCalendar,
  IconCurrencyDollar,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { usePaymentPlans, useCancelPaymentPlan } from '@/hooks/usePortalApi';
import type { PaymentPlan } from '@/types';
import dayjs from 'dayjs';

const STATUS_COLORS: Record<string, string> = {
  active: 'blue',
  completed: 'green',
  cancelled: 'gray',
  defaulted: 'red',
  draft: 'yellow',
};

export default function PaymentPlans() {
  const { data, isLoading } = usePaymentPlans();
  const cancelPlan = useCancelPaymentPlan();
  const [selectedPlan, setSelectedPlan] = useState<PaymentPlan | null>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [cancelOpen, { open: openCancel, close: closeCancel }] = useDisclosure(false);
  const [cancelPlanId, setCancelPlanId] = useState<number | null>(null);

  const plans = data?.items ?? [];

  const activePlans = plans.filter((p) => p.status === 'active');
  const totalRemaining = activePlans.reduce((sum, p) => {
    const paid = p.installments.filter((i) => i.status === 'paid').reduce((s, i) => s + parseFloat(i.amount), 0);
    return sum + parseFloat(p.total_amount) - paid;
  }, 0);

  const handleViewPlan = (plan: PaymentPlan) => {
    setSelectedPlan(plan);
    openDrawer();
  };

  const handleCancelClick = (planId: number) => {
    setCancelPlanId(planId);
    openCancel();
  };

  const handleConfirmCancel = () => {
    if (!cancelPlanId) return;
    cancelPlan.mutate(cancelPlanId, {
      onSuccess: () => {
        notifications.show({ title: 'Plan cancelled', message: 'Payment plan has been cancelled.', color: 'brand' });
        closeCancel();
        closeDrawer();
      },
      onError: () => {
        notifications.show({ title: 'Error', message: 'Unable to cancel plan.', color: 'red' });
      },
    });
  };

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={300} />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Skeleton height={80} />
          <Skeleton height={80} />
        </SimpleGrid>
        <Skeleton height={300} />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader title="Payment Plans" description="Manage your installment payment arrangements" />

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <StatCard
          title="Active Plans"
          value={String(activePlans.length)}
          icon={<IconCalendar size={24} />}
        />
        <StatCard
          title="Total Remaining"
          value={`$${totalRemaining.toFixed(2)}`}
          icon={<IconCurrencyDollar size={24} />}
        />
      </SimpleGrid>

      {plans.length === 0 ? (
        <EmptyState
          icon={<IconCalendar size={30} />}
          title="No payment plans"
          description="You don't have any payment plans set up. You can create one from an invoice detail page."
        />
      ) : (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Plan #</Table.Th>
                <Table.Th>Invoice</Table.Th>
                <Table.Th ta="right">Total</Table.Th>
                <Table.Th ta="right">Per Payment</Table.Th>
                <Table.Th>Frequency</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Next Due</Table.Th>
                <Table.Th>Progress</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {plans.map((plan) => (
                <Table.Tr
                  key={plan.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleViewPlan(plan)}
                >
                  <Table.Td>
                    <Text size="sm" fw={500}>{plan.plan_number}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{plan.invoice_id ? `#${plan.invoice_id}` : '—'}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">${parseFloat(plan.total_amount).toFixed(2)}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">${parseFloat(plan.installment_amount).toFixed(2)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" tt="capitalize">{plan.frequency}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLORS[plan.status] ?? 'gray'} variant="light">
                      {plan.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {plan.next_due_date ? dayjs(plan.next_due_date).format('MMM D, YYYY') : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Progress
                        value={(plan.paid_count / plan.num_installments) * 100}
                        size="sm"
                        w={60}
                      />
                      <Text size="xs" c="dimmed">
                        {plan.paid_count}/{plan.num_installments}
                      </Text>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Installment schedule drawer */}
      <Drawer
        opened={drawerOpen}
        onClose={closeDrawer}
        position="right"
        size="lg"
        title={
          <Text fw={700} size="lg">
            {selectedPlan?.plan_number ?? 'Plan Details'}
          </Text>
        }
      >
        {selectedPlan && (
          <Stack gap="md">
            <Group justify="space-between">
              <Badge color={STATUS_COLORS[selectedPlan.status] ?? 'gray'} variant="light" size="lg">
                {selectedPlan.status}
              </Badge>
              <Text size="sm" c="dimmed" tt="capitalize">
                {selectedPlan.frequency}
              </Text>
            </Group>

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Amount</Text>
                <Text size="sm" fw={500}>${parseFloat(selectedPlan.total_amount).toFixed(2)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Per Installment</Text>
                <Text size="sm" fw={500}>${parseFloat(selectedPlan.installment_amount).toFixed(2)}</Text>
              </div>
            </SimpleGrid>

            <Text fw={600} size="sm" mt="sm">Installment Schedule</Text>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Due Date</Table.Th>
                  <Table.Th ta="right">Amount</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Paid Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {selectedPlan.installments.map((inst) => (
                  <Table.Tr key={inst.id}>
                    <Table.Td><Text size="sm">{inst.installment_number}</Text></Table.Td>
                    <Table.Td><Text size="sm">{dayjs(inst.due_date).format('MMM D, YYYY')}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">${parseFloat(inst.amount).toFixed(2)}</Text></Table.Td>
                    <Table.Td>
                      <Badge
                        color={inst.status === 'paid' ? 'green' : inst.status === 'overdue' ? 'red' : 'gray'}
                        variant="light"
                        size="sm"
                      >
                        {inst.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{inst.paid_at ? dayjs(inst.paid_at).format('MMM D, YYYY') : '—'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {selectedPlan.status === 'active' && (
              <Button
                color="red"
                variant="light"
                onClick={() => handleCancelClick(selectedPlan.id)}
                mt="md"
              >
                Cancel Plan
              </Button>
            )}
          </Stack>
        )}
      </Drawer>

      {/* Cancel confirmation modal */}
      <Modal opened={cancelOpen} onClose={closeCancel} title="Cancel Payment Plan" size="sm">
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to cancel this payment plan? All pending installments will be cancelled.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCancel}>No, Keep It</Button>
            <Button color="red" onClick={handleConfirmCancel} loading={cancelPlan.isPending}>
              Yes, Cancel Plan
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
